// C2 UI Style Guide tokens for inline JSX styles and chart code.
// Mirror of the CSS variables in src/styleguide.css — keep the two in sync.
// Source of truth: docs/C2 UI Style Guide.html (token appendix).

export const BRAND = {
  ink: '#212630',
  blue: '#315DFF',
  blueHover: '#2749D6',
  violet: '#7360F6',
  violet300: '#B78AF8',
  cyan: '#00C4F0',
};

export const INK = {
  900: '#212630',
  700: '#3A4150',
  500: '#5C6473',
  400: '#8A92A0',
  300: '#B8BFC9',
  200: '#D9DDE3',
  150: '#E5E8ED',
  100: '#EDEFF2',
  50: '#F6F7F9',
};

export const SURFACE = '#FFFFFF';
export const CANVAS = '#F6F7F9';

// Semantic status — text / bg / dot triads. A "Secured" tag is always the
// same green, on every screen, in every table.
export const STATUS = {
  success: { text: '#15803D', bg: '#DCFCE7', dot: '#16A34A' },
  warning: { text: '#B45309', bg: '#FEF3C7', dot: '#D97706' },
  orange: { text: '#C2410C', bg: '#FFEDD5', dot: '#EA580C' },
  danger: { text: '#B91C1C', bg: '#FEE2E2', dot: '#DC2626' },
  info: { text: '#1E40AF', bg: '#E9EEFF', dot: '#315DFF' },
  neutral: { text: '#475569', bg: '#EEF1F5', dot: '#8A92A0' },
};

// Finding-volume ramp for the results scatter, smallest → largest:
// blue → yellow → orange → red, all status-pill dot hues. Deliberately no
// green — green means "secured/clean" in this product and must never encode
// a quantity of sensitive findings.
export const SEVERITY_RAMP = [
  STATUS.info.dot,
  STATUS.warning.dot,
  STATUS.orange.dot,
  STATUS.danger.dot,
];

// Categorical chart ramp — brand + semantic hues only. Index into it with
// chartSeriesColor(i); it repeats for series counts beyond its length.
export const CHART_SERIES = [
  '#315DFF', // primary (brand blue)
  '#7360F6', // secondary (violet)
  '#00C4F0', // tertiary (cyan)
  '#B78AF8', // quaternary (violet-300)
  '#16A34A',
  '#D97706',
  '#0891B2',
  '#8A92A0',
];

export const chartSeriesColor = (i) => CHART_SERIES[((i % CHART_SERIES.length) + CHART_SERIES.length) % CHART_SERIES.length];

// Fixed operation → color map. Same color wherever the concept appears.
export const OPERATION = {
  encrypt: { color: '#315DFF', bg: '#E9EEFF' },
  mask: { color: '#7360F6', bg: '#EFEBFE' },
  synthesize: { color: '#00C4F0', bg: '#E6FAFE', text: '#0891B2' },
};

// "Remaining / other" chart segments always use CHART_REMAINDER, never a
// CHART_SERIES color.
export const CHART_REMAINDER = '#D9DDE3';

export const FONT_UI = "'Rubik', system-ui, sans-serif";
export const FONT_MONO =
  "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

export const RADIUS = { sm: 6, md: 10, lg: 14, full: 999 };

export const ELEVATION = {
  e1: '0 1px 2px rgba(33,38,48,.06)',
  e2: '0 4px 14px rgba(33,38,48,.10)',
  e3: '0 12px 34px rgba(33,38,48,.16)',
};

// Map a raw status string (job / source / run states) onto a semantic key.
// Anything unknown stays neutral — decorative color on data creates false
// signals.
export const statusToSemantic = (status) => {
  const s = String(status || '').toUpperCase();
  if (['SECURED', 'MASKED', 'ENCRYPTED', 'COMPLETED', 'SUCCEEDED', 'SUCCESS', 'AVAILABLE', 'ACTIVE', 'CLEAN', 'READY', 'FINISHED'].indexOf(s) >= 0) return 'success';
  if (['CANCELLED', 'CANCELED', 'STOPPED', 'STOPPING', 'TIMEOUT', 'WARNING', 'PARTIAL', 'DEGRADED'].indexOf(s) >= 0) return 'warning';
  if (['ERROR', 'FAILED', 'UNSECURED', 'REJECTED', 'WORKER_REJECTED'].indexOf(s) >= 0) return 'danger';
  if (['RUNNING', 'IN PROGRESS', 'IN_PROGRESS', 'QUEUED', 'PENDING', 'STARTING', 'INITIALIZING', 'PROFILING'].indexOf(s) >= 0) return 'info';
  return 'neutral';
};
